import React, { useEffect, useState, useCallback } from "react";
import { stat } from "@/lib/i18nFormat";
import { api } from "@/lib/api";
import { PageHeader, LoadingBlock, EmptyState } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Gauge, ChevronLeft, ChevronRight } from "lucide-react";

import { useI18n } from "@/context/I18nContext";

const STATE_STYLE: Record<string, string> = {
  overloaded: "bg-red-500 text-white", underused: "bg-amber-500 text-white",
  balanced: "bg-emerald-600 text-white", no_capacity: "bg-muted text-muted-foreground",
  needs_data: "bg-slate-400 text-white",
};

function mondayOf(d: Date) { const x = new Date(d); const wd = (x.getDay() + 6) % 7; x.setDate(x.getDate() - wd); return x.toISOString().slice(0, 10); }

export default function Capacity() {
  const {
    t
  } = useI18n();

  const [stores, setStores] = useState<any[]>([]);
  const [store, setStore] = useState("");
  const [week, setWeek] = useState(mondayOf(new Date()));
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  useEffect(() => { api.get("/org/stores").then((r) => { const s = r.data.stores || []; setStores(s); if (s[0]) setStore(s[0].id); }).catch(() => {}); }, []);
  const load = useCallback(() => {
    if (!store) return;
    setLoading(true);
    api.get("/capacity/overview", { params: { store_id: store, week_start: week } }).then((r) => setData(r.data)).catch(() => setData(null)).finally(() => setLoading(false));
  }, [store, week]);
  useEffect(() => { load(); }, [load]);
  const shiftWeek = (days: number) => { const d = new Date(week); d.setDate(d.getDate() + days); setWeek(mondayOf(d)); };
  return (
    (<div data-testid="capacity-page">
      <PageHeader title="Task Capacity & Workload" subtitle="Schedule-aware view of available labor vs planned task workload (read-only)" testId="capacity-header" />
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <select className="h-9 rounded-md border border-border bg-background px-2 text-sm" value={store} onChange={(e) => setStore(e.target.value)} data-testid="cap-store">
          {stores.map((s) => <option key={s.id} value={s.id}>{t("Store")}{" "}{s.store_number}</option>)}
        </select>
        <div className="flex items-center gap-1">
          <Button size="icon" variant="outline" onClick={() => shiftWeek(-7)} data-testid="cap-prev"><ChevronLeft size={16} /></Button>
          <span className="px-2 text-sm font-medium" data-testid="cap-week">{t("Week of")}{" "}{week}</span>
          <Button size="icon" variant="outline" onClick={() => shiftWeek(7)} data-testid="cap-next"><ChevronRight size={16} /></Button>
        </div>
      </div>
      {loading ? <LoadingBlock /> : !data ? <Card className="p-6"><EmptyState icon={<Gauge size={24} />} title="No data" /></Card> :
        !data.configured ? <Card className="p-6"><EmptyState icon={<Gauge size={24} />} title="Not available" description={data.message || "Capacity planning is unavailable for this store."} /></Card> :
          <>
            <div className="mb-3 grid grid-cols-2 gap-2 sm:grid-cols-4" data-testid="cap-totals">
              <Card className="p-3"><div className="text-xs text-muted-foreground">{t("Available")}</div><div className="text-xl font-semibold">{data.totals.available_hours ?? "—"}h</div></Card>
              <Card className="p-3"><div className="text-xs text-muted-foreground">{t("Planned tasks")}</div><div className="text-xl font-semibold">{data.totals.planned_task_hours}h</div></Card>
              <Card className="p-3"><div className="text-xs text-muted-foreground">{t("Task count")}</div><div className="text-xl font-semibold">{data.totals.task_count}</div></Card>
              <Card className="p-3"><div className="text-xs text-muted-foreground">{t("Missing durations")}</div><div className="text-xl font-semibold">{data.totals.missing_duration_count}</div></Card>
            </div>
            <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3" data-testid="cap-days">
              {data.days.map((d: any) => (
                <Card key={d.date} className="p-3.5" data-testid={`cap-day-${d.date}`}>
                  <div className="mb-1 flex items-center justify-between">
                    <div className="font-medium">{d.weekday} <span className="text-xs text-muted-foreground">{d.date}</span></div>
                    <Badge className={STATE_STYLE[d.state] || "bg-muted"} data-testid={`cap-state-${d.date}`}>{stat(d.state)}</Badge>
                  </div>
                  <div className="text-sm">{d.available_hours ?? "—"}{t("h available ·")}{" "}{d.planned_task_hours}{t("h planned")}{d.headroom_hours !== null ? ` · ${d.headroom_hours}h headroom` : ""}</div>
                  <div className="mt-1 text-xs text-muted-foreground">{d.task_count}{" "}{t("task(s)")}{d.missing_duration_count ? ` · ${d.missing_duration_count} missing est.` : ""}</div>
                  <p className="mt-1.5 text-xs text-muted-foreground" data-testid={`cap-rec-${d.date}`}>{d.recommendation}</p>
                </Card>
              ))}
            </div>
          </>}
    </div>)
  );
}
